import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react'
import { request } from '../api/client'
import { getOrCreateDeviceId, readSession, writeSession, type AuthSession } from '../api/session'
import type { LocationBootstrap, PermissionInfo } from '../api/types'

interface LoginResponse {
  userId?: string | number
  tenantId?: string | number
  accessToken?: string
  refreshToken?: string
  expiresTime?: string
  loginStatus: 'AUTHORIZED' | 'DEVICE_PENDING' | 'DEVICE_REJECTED' | 'SYSTEM_CLOSED'
  deviceRequestId?: string | number
  deviceRequestToken?: string
  loginMessage?: string
  passwordChangeRequired?: boolean
  recoveryMode?: boolean
  errorCode?: number
}

interface Credentials { username: string; password: string }

interface AuthContextValue {
  session: AuthSession | null
  permissionInfo: PermissionInfo | null
  stores: LocationBootstrap | null
  loading: boolean
  contextError: Error | null
  login: (credentials: Credentials) => Promise<LoginResponse>
  completeLogin: (result: LoginResponse) => void
  logout: () => Promise<void>
  reloadContext: () => Promise<void>
  hasPermission: (permission: string) => boolean
  isTenantAdmin: boolean
  setRecoveryMode: (enabled: boolean) => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AuthSession | null>(() => readSession())
  const [permissionInfo, setPermissionInfo] = useState<PermissionInfo | null>(null)
  const [stores, setStores] = useState<LocationBootstrap | null>(null)
  const [loading, setLoading] = useState(Boolean(session))
  const [contextError, setContextError] = useState<Error | null>(null)

  const reloadContext = useCallback(async () => {
    const active = readSession()
    if (!active) {
      setPermissionInfo(null); setStores(null); setContextError(null); setLoading(false); return
    }
    setLoading(true)
    setContextError(null)
    try {
      if (active.recoveryMode) {
        setPermissionInfo({
          user: { id: active.userId, nickname: '租户老板' },
          roles: ['tenant_admin'], permissions: [],
        })
        setStores(null)
      } else {
        const [infoResult, bootstrapResult] = await Promise.allSettled([
          request<PermissionInfo>({ url: '/system/auth/get-permission-info' }),
          request<LocationBootstrap>({ url: '/luxury/location/bootstrap' }),
        ])
        if (infoResult.status === 'rejected') throw infoResult.reason
        if (!infoResult.value?.user) throw new Error('未能读取当前账号信息')
        setPermissionInfo(infoResult.value)
        setStores(bootstrapResult.status === 'fulfilled' ? bootstrapResult.value : null)
      }
    } catch (reason) {
      const error = reason instanceof Error ? reason : new Error('账号信息加载失败')
      setContextError(error)
      throw error
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reloadContext().catch(() => undefined)
  }, [reloadContext, session?.accessToken])
  useEffect(() => {
    const expired = () => { setSession(null); setPermissionInfo(null); setStores(null) }
    window.addEventListener('leshe:session-expired', expired)
    return () => window.removeEventListener('leshe:session-expired', expired)
  }, [])

  const login = useCallback(async (credentials: Credentials) => request<LoginResponse>({
    url: '/system/auth/login', method: 'POST', data: {
      ...credentials,
      deviceId: getOrCreateDeviceId(),
      deviceName: `${navigator.platform || 'Desktop'} Web`,
      devicePlatform: 'WEB',
      deviceOsVersion: navigator.userAgent.slice(0, 64),
      appVersion: '1.0.0',
      captchaVerification: '',
    },
  }), [])

  const completeLogin = useCallback((result: LoginResponse) => {
    if (!result.accessToken || !result.refreshToken || !result.tenantId || !result.userId) {
      throw new Error(result.loginMessage || '登录未完成')
    }
    const next: AuthSession = {
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
      tenantId: String(result.tenantId),
      userId: String(result.userId),
      expiresTime: result.expiresTime,
      recoveryMode: Boolean(result.recoveryMode),
      passwordChangeRequired: Boolean(result.passwordChangeRequired),
    }
    writeSession(next)
    setPermissionInfo(null); setStores(null); setContextError(null); setLoading(true); setSession(next)
  }, [])

  const logout = useCallback(async () => {
    try { await request<boolean>({ url: '/system/auth/logout', method: 'POST' }) } catch { /* revoke locally */ }
    writeSession(null); sessionStorage.removeItem('leshe-admin-store-id')
    setSession(null); setPermissionInfo(null); setStores(null); setContextError(null)
  }, [])

  const setRecoveryMode = useCallback(async (enabled: boolean) => {
    const active = readSession()
    if (!active) return
    const next = { ...active, recoveryMode: enabled }
    writeSession(next); setSession(next)
    if (!enabled) await reloadContext()
  }, [reloadContext])

  const roles = permissionInfo?.roles || []
  const permissions = useMemo(() => permissionInfo?.permissions || [], [permissionInfo?.permissions])
  const isTenantAdmin = roles.includes('tenant_admin')
  const hasPermission = useCallback((permission: string) => isTenantAdmin || permissions.includes(permission),
    [isTenantAdmin, permissions])

  const value = useMemo<AuthContextValue>(() => ({
    session, permissionInfo, stores, loading, contextError, login, completeLogin, logout,
    reloadContext, hasPermission, isTenantAdmin, setRecoveryMode,
  }), [session, permissionInfo, stores, loading, contextError, login, completeLogin, logout,
    reloadContext, hasPermission, isTenantAdmin, setRecoveryMode])
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used within AuthProvider')
  return value
}
